import { window } from "@packages/global";
import type React from "react";
import {
  type AllHTMLAttributes,
  type ReactNode,
  useEffect,
  useRef,
} from "react";
import { ArrowDownIcon } from "../../icons";
import { classNames } from "../../support/formatters/class-formatter";
import { Steps } from "../steps";
import { CarouselSlide } from "./carousel-slide.component";
import styles from "./carousel.module.css";
import { useCarousel } from "./hooks";
import { CarouselProvider } from "./providers";

export type CarouselProps = {
  children: ReactNode;
  arrowsSize?: number;
  arrowsActivated?: boolean;
  withSteps?: boolean;
  hideSlidesCount?: boolean;
  caption?: React.ReactNode;
  className?: string;
  classNameInner?: string;
  classNameArrows?: string;
  classNameArrowBack?: string;
  classNameArrowForward?: string;
} & AllHTMLAttributes<HTMLDivElement>;

export function _Carousel({
  children,
  arrowsSize = 16,
  arrowsActivated = false,
  withSteps = false,
  hideSlidesCount = false,
  caption = "",
  className = "",
  classNameInner = "",
  classNameArrows = "",
  classNameArrowBack = "",
  classNameArrowForward = "",
  ...props
}: CarouselProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const ref = useRef<HTMLDivElement>(null);
  const carouselCtrl = useCarousel();
  const goBack = !arrowsActivated
    ? carouselCtrl.canGoBack && carouselCtrl.hover
    : true;
  const goForward = !arrowsActivated
    ? carouselCtrl.canGoForward && carouselCtrl.hover
    : true;

  useEffect(() => {
    carouselCtrl.setContainerWidth(ref.current?.clientWidth || 0);
  }, []);

  /**
   * Listen to scroll effects
   *
   */
  useEffect(() => {
    if (
      !containerRef.current ||
      typeof containerRef.current?.scrollTo !== "function"
    ) {
      return;
    }

    containerRef.current?.scrollTo({
      top: 0,
      left: carouselCtrl.scrollOffset,
      behavior: "smooth",
    });
  }, [carouselCtrl.scrollOffset]);

  /**
   * Set initial container width
   *
   */
  useEffect(() => {
    function listener() {
      carouselCtrl.setContainerWidth(ref.current?.clientWidth || 0);
    }
    carouselCtrl.setContainerWidth(ref.current?.clientWidth || 0);

    window.addEventListener("resize", listener);

    return () => window.removeEventListener("resize", listener);
  }, []);

  /**
   * Handle mouse enter
   *
   */
  function handleMouseEnter() {
    carouselCtrl.setHover(true);
  }

  /**
   * Handle mouse leave
   *
   */
  function handleMouseLeave() {
    carouselCtrl.setHover(false);
  }

  const navigationRequired: boolean = carouselCtrl.slidesCount > 1;

  return (
    <div
      ref={ref}
      className={`${styles.Container} ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...props}
    >
      <div className={styles.ContentWrapper}>
        {navigationRequired && (
          <>
            {goBack && (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  carouselCtrl.goBack();
                }}
                className={classNames(
                  styles.Button,
                  styles.Left,
                  classNameArrows,
                  classNameArrowBack
                )}
                data-testid="Button_Carousel_Back"
                aria-label="Anterior"
              >
                <ArrowDownIcon size={arrowsSize} />
              </button>
            )}

            {goForward && (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  carouselCtrl.goForward();
                }}
                className={classNames(
                  styles.Button,
                  styles.Right,
                  classNameArrows,
                  classNameArrowForward
                )}
                data-testid="Button_Carousel_Forward"
                aria-label="Próximo"
              >
                <ArrowDownIcon size={arrowsSize} />
              </button>
            )}

            {!hideSlidesCount && (
              <div data-testid="Carousel_Slides_Count" className={styles.Badge}>
                {carouselCtrl.index + 1}/{carouselCtrl.slidesCount}
              </div>
            )}
          </>
        )}
        <div ref={containerRef} className={`${styles.Inner} ${classNameInner}`}>
          {children}
        </div>
      </div>

      <div className={styles.MetadataWrapper}>
        {caption && <span className={styles.Caption}>{caption}</span>}
        {navigationRequired && withSteps && (
          <div className={styles.Steps} data-testid="Carousel_Steps_Navigation">
            <Steps
              steps={carouselCtrl.slides}
              current={carouselCtrl.index}
              goTo={carouselCtrl.goTo}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export function Carousel({ children, ...props }: CarouselProps) {
  return (
    <CarouselProvider>
      <_Carousel {...props}>{children}</_Carousel>
    </CarouselProvider>
  );
}

export namespace Carousel {
  export const Slide = CarouselSlide;
}
